import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, ExternalLink } from "lucide-react";
import {
  adminListPresentations,
  adminSavePresentation,
  adminDeletePresentation,
  adminListAssignments,
  adminSaveAssignment,
} from "@/lib/api/presentations.functions";
import { QUALIFICATIONS } from "@/lib/student-portal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export const Route = createFileRoute("/_authenticated/portal/admin/presentations")({
  component: AdminPresentationsPage,
});

const QUAL_OPTIONS = [{ value: "all", label: "All qualifications" }, ...QUALIFICATIONS];

function AdminPresentationsPage() {
  const listPres = useServerFn(adminListPresentations);
  const savePres = useServerFn(adminSavePresentation);
  const delPres = useServerFn(adminDeletePresentation);
  const listAssign = useServerFn(adminListAssignments);
  const saveAssign = useServerFn(adminSaveAssignment);
  const qc = useQueryClient();

  const pres = useQuery({ queryKey: ["admin-presentations"], queryFn: () => listPres() });
  const assign = useQuery({ queryKey: ["admin-assignments"], queryFn: () => listAssign() });

  const [p, setP] = useState({
    title: "",
    summary: "",
    cover_image_url: "",
    file_url: "",
    embed_url: "",
    speaker_notes: "",
    qualification: "all",
    course_label: "",
    published: true,
  });
  const [a, setA] = useState({
    title: "",
    description: "",
    qualification: "all",
    course_label: "",
    due_at: "",
    max_marks: "100",
  });
  const [busy, setBusy] = useState(false);

  return (
    <div className="space-y-10">
      <header>
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Student portal</p>
        <h2 className="mt-1 font-display text-2xl text-navy">Presentations & assignments</h2>
      </header>

      <section className="grid gap-6 lg:grid-cols-[1fr_360px]">
        <div className="space-y-3">
          <h3 className="font-display text-lg text-navy">Published presentations</h3>
          {pres.isLoading ? (
            <Loader2 className="h-5 w-5 animate-spin text-navy" />
          ) : (pres.data?.presentations.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing published yet.</p>
          ) : (
            pres.data!.presentations.map((row: any) => (
              <div
                key={row.id}
                className="flex items-start justify-between gap-3 rounded-lg border border-border bg-card p-4"
              >
                <div>
                  <h4 className="font-display text-base text-navy">{row.title}</h4>
                  <p className="text-xs text-muted-foreground">
                    {row.course_label || "—"} · {row.published ? "Published" : "Draft"}
                  </p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <a
                    href={`/presentations/${row.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="View public page"
                    className="rounded-md border border-input p-1.5 hover:border-navy"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                  <button
                    type="button"
                    aria-label="Delete presentation"
                    onClick={async () => {
                      await delPres({ data: { id: row.id } });
                      qc.invalidateQueries({ queryKey: ["admin-presentations"] });
                    }}
                    className="rounded-md border border-input p-1.5 text-destructive hover:border-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            try {
              await savePres({ data: p as any });
              toast.success("Presentation saved");
              setP({ ...p, title: "", summary: "", cover_image_url: "", file_url: "", embed_url: "", speaker_notes: "" });
              qc.invalidateQueries({ queryKey: ["admin-presentations"] });
            } catch (err: any) {
              toast.error("Could not save", { description: err?.message });
            } finally {
              setBusy(false);
            }
          }}
          className="h-fit space-y-3 rounded-lg border border-border bg-card p-5"
        >
          <h3 className="font-display text-lg text-navy">New presentation</h3>
          <div>
            <Label htmlFor="ap_title">Title</Label>
            <Input id="ap_title" value={p.title} onChange={(e) => setP({ ...p, title: e.target.value })} required />
          </div>
          <div>
            <Label htmlFor="ap_sum">Summary (used for Facebook preview)</Label>
            <Textarea id="ap_sum" rows={3} value={p.summary} onChange={(e) => setP({ ...p, summary: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="ap_cover">Cover image URL (https)</Label>
            <Input id="ap_cover" value={p.cover_image_url} onChange={(e) => setP({ ...p, cover_image_url: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="ap_file">Slides download URL</Label>
            <Input id="ap_file" value={p.file_url} onChange={(e) => setP({ ...p, file_url: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="ap_embed">Embed URL (Canva / Slides)</Label>
            <Input id="ap_embed" value={p.embed_url} onChange={(e) => setP({ ...p, embed_url: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="ap_qual">Qualification</Label>
            <select
              id="ap_qual"
              value={p.qualification}
              onChange={(e) => setP({ ...p, qualification: e.target.value })}
              className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              {QUAL_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="ap_course">Course label</Label>
            <Input id="ap_course" value={p.course_label} onChange={(e) => setP({ ...p, course_label: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="ap_notes">Speaker notes</Label>
            <Textarea id="ap_notes" rows={3} value={p.speaker_notes} onChange={(e) => setP({ ...p, speaker_notes: e.target.value })} />
          </div>
          <Button type="submit" disabled={busy} className="w-full bg-navy hover:bg-navy/90">
            <Plus className="mr-2 h-4 w-4" /> {busy ? "Saving…" : "Publish presentation"}
          </Button>
        </form>
      </section>

      <section className="grid gap-6 lg:grid-cols-[1fr_360px]">
        <div className="space-y-3">
          <h3 className="font-display text-lg text-navy">Assignments</h3>
          {assign.isLoading ? (
            <Loader2 className="h-5 w-5 animate-spin text-navy" />
          ) : (assign.data?.assignments.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">No assignments created yet.</p>
          ) : (
            assign.data!.assignments.map((row: any) => (
              <div key={row.id} className="rounded-lg border border-border bg-card p-4">
                <h4 className="font-display text-base text-navy">{row.title}</h4>
                <p className="text-xs text-muted-foreground">
                  {row.course_label || "—"} · due{" "}
                  {row.due_at ? new Date(row.due_at).toLocaleString() : "no deadline"} ·{" "}
                  {row.qualification}
                </p>
              </div>
            ))
          )}
        </div>

        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            try {
              await saveAssign({
                data: {
                  title: a.title,
                  description: a.description,
                  qualification: a.qualification,
                  course_label: a.course_label,
                  due_at: a.due_at ? new Date(a.due_at).toISOString() : null,
                  max_marks: Number(a.max_marks) || 100,
                  published: true,
                },
              });
              toast.success("Assignment published");
              setA({ ...a, title: "", description: "", due_at: "" });
              qc.invalidateQueries({ queryKey: ["admin-assignments"] });
            } catch (err: any) {
              toast.error("Could not save", { description: err?.message });
            } finally {
              setBusy(false);
            }
          }}
          className="h-fit space-y-3 rounded-lg border border-border bg-card p-5"
        >
          <h3 className="font-display text-lg text-navy">New assignment</h3>
          <div>
            <Label htmlFor="aa_title">Title</Label>
            <Input id="aa_title" value={a.title} onChange={(e) => setA({ ...a, title: e.target.value })} required />
          </div>
          <div>
            <Label htmlFor="aa_desc">Instructions</Label>
            <Textarea id="aa_desc" rows={4} value={a.description} onChange={(e) => setA({ ...a, description: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="aa_qual">Qualification</Label>
            <select
              id="aa_qual"
              value={a.qualification}
              onChange={(e) => setA({ ...a, qualification: e.target.value })}
              className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              {QUAL_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="aa_course">Course label</Label>
            <Input id="aa_course" value={a.course_label} onChange={(e) => setA({ ...a, course_label: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="aa_due">Due date</Label>
            <Input id="aa_due" type="datetime-local" value={a.due_at} onChange={(e) => setA({ ...a, due_at: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="aa_marks">Maximum marks</Label>
            <Input id="aa_marks" type="number" min={1} value={a.max_marks} onChange={(e) => setA({ ...a, max_marks: e.target.value })} />
          </div>
          <Button type="submit" disabled={busy} className="w-full bg-navy hover:bg-navy/90">
            <Plus className="mr-2 h-4 w-4" /> {busy ? "Saving…" : "Publish assignment"}
          </Button>
        </form>
      </section>
    </div>
  );
}
